import { useIsFocused } from '@react-navigation/native';
import { useForbidBackAction } from 'app/hooks/use-forbid-back-action';
import { useScopedTranslation } from 'app/hooks/i18n';
import { Event, track, trackOnFacebook } from 'app/lib/analytics';
import { recordError } from 'app/lib/monitoring';
import { useOnePreference, useUpsertOnePreference } from 'app/network/preferences';
import { useUser } from 'app/network/user';
import { navigateToPostSignupFlow } from 'app/screens/intro/navigator-helper';
import type { SpecificNavigationProp } from 'app/types/navigation';
import { TrialModalType } from 'app/types/preferences';
import { WhiteLabel } from 'app/types/white-labels';
import { isWhiteLabel } from 'app/utils/helpers';
import { isMobileNative } from 'app/utils/web-helpers';
import { AppEventsLogger } from 'react-native-fbsdk-next';
import { useRef, useState } from 'react';

function useFinishedIntro(navigation: SpecificNavigationProp<'presentCommunity'>) {
  const userModalPreference = useOnePreference('trialModal').data?.value;
  const { mutateAsync: updateModalPreference } = useUpsertOnePreference('trialModal');
  const userQuery = useUser();
  const finishingRef = useRef(false);
  const [isFinishing, setIsFinishing] = useState(false);

  const finishedIntro = async (finished: boolean) => {
    if (finishingRef.current) return;
    finishingRef.current = true;
    setIsFinishing(true);
    if (!finished) {
      track(Event.GiveUpOnboarding, { where: 'present-community' });
    }
    if (finished || userModalPreference === TrialModalType.once) {
      try {
        await updateModalPreference(TrialModalType.never);
      } catch (error) {
        recordError(error);
      }
      if (isMobileNative) {
        trackOnFacebook(AppEventsLogger.AppEvents.CompletedTutorial, {
          [AppEventsLogger.AppEventParams.Success]: finished ? 'true' : 'false',
        });
      }
    }

    if (isWhiteLabel(WhiteLabel.HauteGaspesie)) {
      navigation.navigate(
        'intro',
        {
          screen: 'quickInterests',
          params: { fromHauteGaspesie: true },
        },
        { pop: true },
      );
      return;
    }

    navigateToPostSignupFlow(navigation, userQuery.data?.featureFlags, userQuery.isPending);
  };

  return { finishedIntro, isFinishing };
}

export function usePresentCommunityScreen({ navigation }: { navigation: SpecificNavigationProp<'presentCommunity'> }) {
  const isFocused = useIsFocused();
  useForbidBackAction(isFocused);
  const { t } = useScopedTranslation();
  const { finishedIntro, isFinishing } = useFinishedIntro(navigation);

  return {
    t,
    finishedIntro,
    isFinishing,
  };
}
